import React, { forwardRef } from 'react';
import { FilterOutlined, SearchOutlined } from '@ant-design/icons';

import { useUIColors } from '../../theme';
import { cx, defineRecipe, useRecipe, type Extensible, type Styleable } from '../../variants';

/**
 * EAA-1821: the small square control that lives in a table header beside a
 * column label and opens that column's filter.
 *
 * It is a component rather than "a `Button` with some styles" because its two
 * states carry meaning a caller must not be free to reinterpret: inactive is
 * transparent and muted (an affordance), active is a brand wash (a REPORT that
 * this column is currently narrowing the table). Every hand-styled copy of it
 * so far got the active state subtly different, so an operator could not tell
 * at a glance which columns were filtered.
 *
 * `icon` only picks the glyph — a funnel for a value filter, a magnifier for a
 * free-text column search. Everything else about the box is identical, which is
 * what keeps a header row looking like one control repeated.
 */

export type ColumnFilterIcon = Extensible<'filter' | 'search'>;

export const columnFilterButtonRecipe = defineRecipe('columnFilterButton', 'ui-column-filter', {
    variants: {
        icon: { filter: 'ui-column-filter--filter', search: 'ui-column-filter--search' },
        active: { yes: 'ui-column-filter--active', no: '' },
    },
    defaultVariants: { icon: 'filter', active: 'no' },
});

export interface ColumnFilterButtonProps extends Styleable {
    /** Which glyph — and therefore which kind of filter this opens. */
    icon?: ColumnFilterIcon;
    /** This column currently narrows the table. */
    active?: boolean;
    /** Reflected as `aria-expanded`; supplied by `FilterPopover`'s trigger state. */
    open?: boolean;
    /** Tooltip AND accessible name — the box has no visible label. */
    title: string;
    onClick: () => void;
    /** Box size in px. The glyph scales with it. */
    size?: number;
}

export const ColumnFilterButton = forwardRef<HTMLButtonElement, ColumnFilterButtonProps>(
    function ColumnFilterButton(
        {
            icon = 'filter',
            active = false,
            open = false,
            title,
            onClick,
            size = 26,
            className,
            style,
        },
        ref
    ) {
        const colors = useUIColors();
        const recipeClassName = useRecipe(columnFilterButtonRecipe, {
            icon,
            active: active ? 'yes' : 'no',
        });

        const Glyph = icon === 'search' ? SearchOutlined : FilterOutlined;

        return (
            <button
                ref={ref}
                type="button"
                className={cx(recipeClassName, className)}
                title={title}
                aria-label={title}
                aria-haspopup={icon === 'search' ? 'dialog' : 'listbox'}
                aria-expanded={open}
                onClick={onClick}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 'none',
                    width: size,
                    height: size,
                    padding: 0,
                    border: 'none',
                    borderRadius: 6,
                    // EAA-1821: the active wash is the brand at ~10%, written as a
                    // color-mix on the SAME brand token the icon takes, so an
                    // org-rebranded primary carries the wash with it.
                    background: active
                        ? `color-mix(in srgb, ${colors.primary} 10%, transparent)`
                        : 'transparent',
                    color: active ? colors.primary : colors.textMuted,
                    cursor: 'pointer',
                    lineHeight: 1,
                    transition: 'background 120ms ease-out, color 120ms ease-out',
                    ...style,
                }}
            >
                <Glyph style={{ fontSize: Math.round(size * 0.46) }} />
            </button>
        );
    }
);

export default ColumnFilterButton;
